import * as React from 'react';
import ReactSelect from 'react-select';
import classnames from 'classnames';

import 'react-select/dist/react-select.css';
import './style.scss';

export interface IMultiSelectProps {
  name: string;
  value: any;
  options: Array<{ value: any; label: string }>;
  onChange: TButtonOnClick;
  onBlur?: TButtonOnClick;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  /** Accessible name for the listbox (WCAG: ARIA input fields must have an accessible name). */
  ariaLabelForListbox?: string;
}

interface IMultiSelectState {
  menuIsOpen: boolean;
}

export default class MultiSelect extends React.Component<IMultiSelectProps, IMultiSelectState> {
  public static defaultProps = {
    name: '',
    value: '',
    clearable: false,
    onChange: null,
    placeholder: 'Select Option',
    options: null,
    disabled: false,
  };

  private containerRef = React.createRef<HTMLDivElement>();

  state: IMultiSelectState = { menuIsOpen: false };

  componentDidUpdate(_prevProps: IMultiSelectProps, prevState: IMultiSelectState) {
    if (this.state.menuIsOpen && !prevState.menuIsOpen && this.containerRef.current) {
      const listbox = this.containerRef.current.querySelector('.Select-menu');
      if (listbox) {
        if (this.props.ariaLabelForListbox && listbox.getAttribute('aria-label') !== this.props.ariaLabelForListbox) {
          listbox.setAttribute('aria-label', this.props.ariaLabelForListbox);
        }
        // WCAG: scrollable region must have keyboard access — ensure listbox is focusable (tabindex="0" puts it in tab order)
        listbox.setAttribute('tabindex', '0');
      }
    }
  }

  onChange = (e) => {
    const newValue = e.map((option) => option.value);

    this.props.onChange({
      target: { name: this.props.name, value: newValue },
      currentTarget: { name: this.props.name, value: newValue },
    });
  };

  renderOption = (option) => {
    const isChecked = this.props.value.includes(option.value);
    return (
      <div className={classnames('Select-custom-option', 'multi-select-option')}>
        <span
          className={classnames('multi-select-option__check', { 'is-checked': isChecked })}
          aria-hidden="true"
        />
        <span className="multi-select-option__label">{option.label}</span>
      </div>
    );
  };

  renderInput = () => {
    const { value, options } = this.props;
    const selectedLabels = options
      ? options.filter((option) => value.includes(option.value)).map((selectedOption) => selectedOption.label)
      : [];
    const selectedLabelsText = selectedLabels.join(', ');

    return (
      <label title={selectedLabelsText} className={classnames('Select-custom-input')}>
        {selectedLabelsText}
      </label>
    );
  };

  render() {
    const { name, options, disabled, value, placeholder } = this.props;
    const nonClearableOptions = options
      ? options.map((option) => ({
          ...option,
          clearableValue: false,
        }))
      : [];

    return (
      <div ref={this.containerRef}>
        <ReactSelect
          className={classnames('multi-select')}
          value={value}
          name={name}
          options={nonClearableOptions}
          clearable={false}
          onChange={this.onChange}
          onBlur={this.props.onBlur}
          onOpen={() => this.setState({ menuIsOpen: true })}
          onClose={() => this.setState({ menuIsOpen: false })}
          placeholder={placeholder}
          disabled={disabled}
          searchable={false}
          multi={true}
          closeOnSelect={false}
          removeSelected={false}
          optionRenderer={this.renderOption}
          inputRenderer={this.renderInput}
        />
      </div>
    );
  }
}
